import * as React from 'react';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import styled from 'styled-components';
import WhatIsCountdownPopover from '@/components/WhatIsCountdownPopover/WhatIsCountdownPopover';

type Props = React.PropsWithChildren<{
    timeStamp: number;
}>;

/**
 * Shows a countdown starting at 21 seconds for the current item
 */
const BidCountDown = ({ children, timeStamp }: Props) => {
    const [count, setCount] = React.useState<number>(21);
    const countRef = React.useRef<number>(count);
    countRef.current = count;

    React.useEffect(() => {
        const timer = setInterval(() => {
            if (countRef.current > 0) {
                setCount(countRef.current - 1);
                countRef.current = countRef.current - 1;
            }
        }, 1000);
        return () => {
            clearInterval(timer);
        };
    }, []);

    React.useEffect(() => {
        setCount(timeStamp < 0 ? 4 : 21);
    }, [children, timeStamp]);

    return (
        <CountDownContainer>
            {count < 6 ? (
                <>
                    <BarDiv>
                        <ProgressDiv count={count} />
                    </BarDiv>
                    <MessageDiv>
                        <TextSpan aria-label="time left to bid">
                            <FormattedMessage
                                id="itemBidding.leftToBid"
                                values={{
                                    count: <CountSpan key="bid-count-down-count">{count}</CountSpan>,
                                }}
                            />
                        </TextSpan>
                        <WhatIsCountdownPopover />
                    </MessageDiv>
                </>
            ) : (
                children && <ChildWrapper>{children}</ChildWrapper>
            )}
        </CountDownContainer>
    );
};

export default BidCountDown;

const CountDownContainer = styled.div`
    width: 100%;
`;

const BarDiv = styled.div`
    height: 10px;
    background-color: ${({ theme }) => theme.colors.grey400};
    margin-bottom: 10px;
    position: relative;
`;

const ProgressDiv = styled.div<{ count }>`
    height: 10px;
    background-color: ${({ theme }) => theme.colors.red100};
    position: absolute;
    right: 0;
    transition: all 0.2s ease-in;
    width: ${(props) => `${(props.count / 5) * 100}%`};
`;

const MessageDiv = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`;

const TextSpan = styled.span`
    display: inline-block;
    min-width: 200px;
`;

const CountSpan = styled.span`
    color: ${({ theme }) => theme.colors.red100};
    font-weight: 600;
`;

const ChildWrapper = styled.div`
    margin-top: 20px;
`;
